import { useEffect, useState } from 'react';
import {
  getPosts, createPost, updatePost, deletePost,
  getComments, deleteComment, addComment,
  getPrefixOptions, savePrefixOptions,
} from '../services/boardService';
import { useAuthStore } from '../stores/authStore';
import { formatTimestamp } from '../utils/time';
import UserDetailModal from '../components/common/UserDetailModal';
import type { BoardPost, BoardComment } from '../types';

export default function BoardPage() {
  const { adminUser } = useAuthStore();
  const [lang, setLang] = useState('ko');
  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [prefixes, setPrefixes] = useState<{ key: string; label: string }[]>([]);
  const [showPrefixMgr, setShowPrefixMgr] = useState(false);

  // View mode
  const [viewPost, setViewPost] = useState<BoardPost | null>(null);
  const [viewComments, setViewComments] = useState<BoardComment[]>([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [newComment, setNewComment] = useState('');

  // Edit mode
  const [editMode, setEditMode] = useState<'create' | 'edit' | null>(null);
  const [editPost, setEditPost] = useState<BoardPost | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formPrefix, setFormPrefix] = useState('none');
  const [formIsNotice, setFormIsNotice] = useState(false);
  const [formIsReleaseNote, setFormIsReleaseNote] = useState(false);

  // User modal
  const [showUserModal, setShowUserModal] = useState<string | null>(null);

  useEffect(() => { loadPosts(); loadPrefixes(); }, [lang]);

  async function loadPosts() {
    setLoading(true);
    try { setPosts(await getPosts(lang, 200)); }
    finally { setLoading(false); }
  }

  async function loadPrefixes() {
    setPrefixes(await getPrefixOptions(lang));
  }

  // View post
  async function handleViewPost(post: BoardPost) {
    setViewPost(post);
    setViewLoading(true);
    const comments = await getComments(lang, post.id);
    setViewComments(comments);
    setViewLoading(false);
  }

  async function handleAddComment() {
    if (!viewPost || !newComment.trim() || !adminUser) return;
    await addComment(lang, viewPost.id, {
      content: newComment,
      authorId: adminUser.uid,
      authorNickname: adminUser.nickname,
    });
    setNewComment('');
    const comments = await getComments(lang, viewPost.id);
    setViewComments(comments);
    loadPosts();
  }

  async function handleDeleteComment(commentId: string) {
    if (!viewPost || !confirm('댓글을 삭제하시겠습니까?')) return;
    await deleteComment(lang, viewPost.id, commentId);
    const comments = await getComments(lang, viewPost.id);
    setViewComments(comments);
    loadPosts();
  }

  // Create/Edit
  function openCreate() {
    setEditMode('create');
    setEditPost(null);
    setFormTitle(''); setFormContent(''); setFormPrefix('none');
    setFormIsNotice(false); setFormIsReleaseNote(false);
  }

  function openEdit(post: BoardPost) {
    setEditMode('edit');
    setEditPost(post);
    setFormTitle(post.title);
    setFormContent(post.content);
    setFormPrefix(post.prefix);
    setFormIsNotice(post.isNotice);
    setFormIsReleaseNote(post.isReleaseNote);
  }

  async function handleSave() {
    if (!formTitle.trim() || !adminUser) return;
    if (editMode === 'create') {
      await createPost(lang, {
        title: formTitle, content: formContent, prefix: formPrefix,
        authorId: adminUser.uid, authorNickname: adminUser.nickname,
        isNotice: formIsNotice, isReleaseNote: formIsReleaseNote,
      });
    } else if (editMode === 'edit' && editPost) {
      await updatePost(lang, editPost.id, {
        title: formTitle, content: formContent,
        prefix: formPrefix as BoardPost['prefix'],
        isNotice: formIsNotice, isReleaseNote: formIsReleaseNote,
      } as Partial<BoardPost>);
    }
    setEditMode(null);
    loadPosts();
  }

  async function handleDelete(postId: string) {
    if (!confirm('게시글을 삭제하시겠습니까?')) return;
    await deletePost(lang, postId);
    loadPosts();
  }

  const filtered = search
    ? posts.filter(p => p.title.toLowerCase().includes(search.toLowerCase()) || p.authorNickname.toLowerCase().includes(search.toLowerCase()))
    : posts;

  const prefixLabel = (key: string) => prefixes.find(p => p.key === key)?.label ?? key;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">게시판 관리</h1>
        <div className="flex gap-2">
          <select value={lang} onChange={e => setLang(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg">
            <option value="ko">한국어</option>
            <option value="en">English</option>
          </select>
          <button onClick={() => setShowPrefixMgr(true)} className="px-3 py-1.5 text-sm bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100">말머리 관리</button>
          <button onClick={openCreate} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">글 쓰기</button>
          <button onClick={loadPosts} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">새로고침</button>
        </div>
      </div>

      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="제목, 작성자 검색..."
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4"
      />

      {/* View Post Modal */}
      {viewPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setViewPost(null)}>
          <div className="bg-white rounded-xl w-full max-w-3xl mx-4 max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Post header */}
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 text-xs bg-gray-100 rounded">{prefixLabel(viewPost.prefix)}</span>
                {viewPost.isNotice && <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">공지</span>}
                {viewPost.isReleaseNote && <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">릴리즈노트</span>}
              </div>
              <h2 className="text-xl font-bold">{viewPost.title}</h2>
              <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                <button onClick={() => setShowUserModal(viewPost.authorId)} className="text-blue-600 hover:underline">{viewPost.authorNickname}</button>
                <span>{formatTimestamp(viewPost.createdAt)}</span>
                <span>조회 {viewPost.views ?? 0}</span>
                <span>좋아요 {viewPost.likeCount}</span>
              </div>
            </div>

            {/* Post content */}
            <div className="flex-1 overflow-y-auto p-5">
              <div className="whitespace-pre-wrap text-sm leading-relaxed min-h-[100px]">{viewPost.content}</div>

              {/* Comments */}
              <div className="mt-6 border-t border-gray-200 pt-4">
                <h3 className="font-semibold text-sm mb-3">댓글 ({viewComments.length})</h3>
                {viewLoading ? (
                  <p className="text-gray-400 text-sm">로딩 중...</p>
                ) : (
                  <div className="space-y-3">
                    {viewComments.map(c => (
                      <div key={c.id} className="flex justify-between items-start p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <button onClick={() => setShowUserModal(c.authorId)} className="text-sm font-medium text-blue-600 hover:underline">{c.authorNickname}</button>
                            <span className="text-xs text-gray-400">{formatTimestamp(c.createdAt, 'short')}</span>
                          </div>
                          <p className="text-sm">{c.content}</p>
                        </div>
                        <button onClick={() => handleDeleteComment(c.id)} className="text-xs text-red-500 hover:underline flex-shrink-0 ml-2">삭제</button>
                      </div>
                    ))}
                    {viewComments.length === 0 && <p className="text-gray-400 text-sm">댓글이 없습니다.</p>}
                  </div>
                )}

                {/* Add comment */}
                <div className="mt-3 flex gap-2">
                  <input
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddComment()}
                    placeholder="댓글 작성..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button onClick={handleAddComment} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">작성</button>
                </div>
              </div>
            </div>

            {/* Post actions */}
            <div className="p-4 border-t border-gray-200 flex gap-2">
              <button onClick={() => { openEdit(viewPost); setViewPost(null); }} className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded">수정</button>
              <button onClick={() => { handleDelete(viewPost.id); setViewPost(null); }} className="px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded">삭제</button>
              <button onClick={() => setViewPost(null)} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded ml-auto">닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {editMode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="font-semibold mb-4">{editMode === 'edit' ? '글 수정' : '글 쓰기'}</h3>
            <div className="space-y-3">
              <div className="flex gap-2">
                <select value={formPrefix} onChange={e => setFormPrefix(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  {prefixes.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
                <input value={formTitle} onChange={e => setFormTitle(e.target.value)} placeholder="제목" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <textarea value={formContent} onChange={e => setFormContent(e.target.value)} placeholder="내용" rows={10} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none" />
              <div className="flex gap-4">
                <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={formIsNotice} onChange={e => setFormIsNotice(e.target.checked)} /> 공지</label>
                <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={formIsReleaseNote} onChange={e => setFormIsReleaseNote(e.target.checked)} /> 릴리즈노트</label>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setEditMode(null)} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg">취소</button>
                <button onClick={handleSave} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg">{editMode === 'edit' ? '수정' : '작성'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prefix Manager Modal */}
      {showPrefixMgr && (
        <PrefixManager
          lang={lang}
          prefixes={prefixes}
          onSave={async (newPrefixes) => { await savePrefixOptions(lang, newPrefixes); await loadPrefixes(); setShowPrefixMgr(false); }}
          onClose={() => setShowPrefixMgr(false)}
        />
      )}

      {/* Posts Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">말머리</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">제목</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">작성자</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">댓글</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">조회</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">작성일</th>
              <th className="px-3 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">로딩 중...</td></tr>
            ) : filtered.map(post => (
              <tr key={post.id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded">{prefixLabel(post.prefix)}</span>
                  {post.isNotice && <span className="ml-1 text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">공지</span>}
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => handleViewPost(post)} className="font-medium text-blue-700 hover:underline text-left">{post.title}</button>
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => setShowUserModal(post.authorId)} className="text-gray-600 hover:text-blue-600 hover:underline">{post.authorNickname}</button>
                </td>
                <td className="px-3 py-2">{post.commentCount}</td>
                <td className="px-3 py-2 text-gray-500">{post.views ?? 0}</td>
                <td className="px-3 py-2 text-gray-500">{formatTimestamp(post.createdAt, 'short')}</td>
                <td className="px-3 py-2 flex gap-2">
                  <button onClick={() => openEdit(post)} className="text-xs text-blue-600 hover:underline">수정</button>
                  <button onClick={() => handleDelete(post.id)} className="text-xs text-red-600 hover:underline">삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t border-gray-200 text-sm text-gray-500">{filtered.length}개 게시글</div>
      </div>

      {/* User Modal */}
      {showUserModal && <UserDetailModal uid={showUserModal} onClose={() => setShowUserModal(null)} />}
    </div>
  );
}

function PrefixManager({ lang, prefixes, onSave, onClose }: {
  lang: string;
  prefixes: { key: string; label: string }[];
  onSave: (p: { key: string; label: string }[]) => void;
  onClose: () => void;
}) {
  const [items, setItems] = useState([...prefixes]);
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <h3 className="font-semibold mb-4">말머리 관리 ({lang})</h3>
        <div className="space-y-2 mb-4">
          {items.map(item => (
            <div key={item.key} className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm"><span className="font-mono text-gray-400">{item.key}</span> - {item.label}</span>
              <button onClick={() => setItems(items.filter(i => i.key !== item.key))} className="text-xs text-red-500">삭제</button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mb-4">
          <input value={newKey} onChange={e => setNewKey(e.target.value)} placeholder="key" className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" />
          <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="label" className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" />
          <button onClick={() => { if (newKey && newLabel) { setItems([...items, { key: newKey, label: newLabel }]); setNewKey(''); setNewLabel(''); }}} className="px-3 py-1 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded">추가</button>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg">취소</button>
          <button onClick={() => onSave(items)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg">저장</button>
        </div>
      </div>
    </div>
  );
}
